import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Button,
  Table,
  Form,
  InputGroup,
  FormControl,
  Spinner,
  Tabs,
  Tab,
} from "react-bootstrap";
import moment from "moment";
import { FaSearch } from "react-icons/fa";
import { HiMiniWallet } from "react-icons/hi2";

const HubWalletCredit = () => {
  // "never-ordered" targets registered-but-never-ordered users in the hub;
  // "all" targets every user whose primary address is in the hub.
  const [mode, setMode] = useState("never-ordered");

  const [hubs, setHubs] = useState([]);
  const [selectedHubId, setSelectedHubId] = useState("");

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [selectedIds, setSelectedIds] = useState([]);

  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Fetch hub list for the dropdown
  useEffect(() => {
    const getHubs = async () => {
      try {
        const res = await axios.get("https://dailydish.in/api/Hub/hubs");
        setHubs(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("Failed to fetch hubs:", error);
      }
    };
    getHubs();
  }, []);

  // Fetch users whenever the hub or the active tab changes
  useEffect(() => {
    if (!selectedHubId) {
      setUsers([]);
      setSelectedIds([]);
      return;
    }
    const endpoint =
      mode === "all"
        ? "https://dailydish.in/api/User/hub-users"
        : "https://dailydish.in/api/User/hub-never-ordered";
    const getUsers = async () => {
      setLoadingUsers(true);
      setSelectedIds([]);
      try {
        const res = await axios.get(endpoint, {
          params: { hubId: selectedHubId },
        });
        setUsers(res.data?.users || []);
      } catch (error) {
        console.error("Failed to fetch hub users:", error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    getUsers();
  }, [selectedHubId, mode]);

  const filteredUsers = useMemo(() => {
    const q = searchQuery.toLowerCase();
    let filtered = !searchQuery
      ? users
      : users.filter(
          (u) =>
            (u.Fname || "").toLowerCase().includes(q) ||
            String(u.Mobile || "").includes(q),
        );

    if (fromDate) {
      const from = moment(fromDate, "YYYY-MM-DD").startOf("day");
      filtered = filtered.filter((u) =>
        moment(u.createdAt).isSameOrAfter(from),
      );
    }
    if (toDate) {
      const to = moment(toDate, "YYYY-MM-DD").endOf("day");
      filtered = filtered.filter((u) => moment(u.createdAt).isSameOrBefore(to));
    }

    return [...filtered].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );
  }, [users, searchQuery, fromDate, toDate]);

  const allFilteredSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((u) => selectedIds.includes(u._id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) =>
        prev.filter((id) => !filteredUsers.some((u) => u._id === id)),
      );
    } else {
      setSelectedIds((prev) =>
        Array.from(new Set([...prev, ...filteredUsers.map((u) => u._id)])),
      );
    }
  };

  const toggleUser = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const canSubmit =
    selectedHubId && selectedIds.length > 0 && Number(amount) > 0;

  const handleSubmit = async () => {
    const hubName = hubs.find((h) => h._id === selectedHubId)?.hubName || "";
    const confirmed = window.confirm(
      `Credit ₹${amount} to ${selectedIds.length} user(s) in "${hubName}"?\n\nTotal amount: ₹${(
        Number(amount) * selectedIds.length
      ).toFixed(2)}\n\nThis cannot be undone.`,
    );
    if (!confirmed) return;

    setSubmitting(true);
    try {
      const res = await axios.post(
        "https://dailydish.in/api/wallet/bulk-credit",
        {
          userIds: selectedIds,
          amount: Number(amount),
          description,
          expiryDays: expiryDate,
        },
      );
      alert(
        `Wallets credited successfully! ${res.data?.creditedCount || selectedIds.length} user(s) credited and notified over WhatsApp.`,
      );
      setSelectedIds([]);
      setAmount("");
      setDescription("");
      setExpiryDate("");
    } catch (error) {
      console.error("Error bulk crediting wallets:", error);
      alert(
        error.response?.data?.error ||
          "Failed to credit wallets. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mt-5">
      <h2 className="text-center mb-4">
        <HiMiniWallet className="header-c me-2" /> Hub Wallet Credit
      </h2>

      <Tabs
        activeKey={mode}
        onSelect={(key) => {
          setMode(key);
          setSearchQuery("");
          setFromDate("");
          setToDate("");
        }}
        className="mb-3"
      >
        <Tab eventKey="never-ordered" title="Never-Ordered Users" />
        <Tab eventKey="all" title="All Users in Hub" />
      </Tabs>

      <div className="row mb-4">
        <div className="col-md-4 mb-2">
          <Form.Label>Hub</Form.Label>
          <Form.Select
            value={selectedHubId}
            onChange={(e) => setSelectedHubId(e.target.value)}
          >
            <option value="">Select a hub</option>
            {hubs.map((hub) => (
              <option key={hub._id} value={hub._id}>
                {hub.hubName}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-4 mb-2">
          <Form.Label>Search</Form.Label>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <FormControl
              placeholder="Search by name or mobile..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={!selectedHubId}
            />
          </InputGroup>
        </div>
        <div className="col-md-2 mb-2">
          <Form.Label>Registered From</Form.Label>
          <Form.Control
            type="date"
            value={fromDate}
            max={toDate || undefined}
            onChange={(e) => setFromDate(e.target.value)}
            disabled={!selectedHubId}
          />
        </div>
        <div className="col-md-2 mb-2">
          <Form.Label>Registered To</Form.Label>
          <Form.Control
            type="date"
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => setToDate(e.target.value)}
            disabled={!selectedHubId}
          />
        </div>
      </div>

      {selectedHubId && (
        <>
          <Table
            striped
            bordered
            hover
            responsive
            className="text-center shadow-sm"
          >
            <thead className="table-dark">
              <tr>
                <th>
                  <Form.Check
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    disabled={filteredUsers.length === 0}
                  />
                </th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Registered On</th>
                <th>Current Wallet Balance</th>
              </tr>
            </thead>
            <tbody>
              {loadingUsers ? (
                <tr>
                  <td colSpan={5}>
                    <Spinner animation="border" variant="primary" />
                  </td>
                </tr>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((u) => (
                  <tr key={u._id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedIds.includes(u._id)}
                        onChange={() => toggleUser(u._id)}
                      />
                    </td>
                    <td>{u.Fname || "Unknown"}</td>
                    <td>{u.Mobile}</td>
                    <td>{moment(u.createdAt).format("lll")}</td>
                    <td className="text-success">
                      ₹{Number(u.walletBalance || 0).toFixed(2)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5}>
                    {mode === "all"
                      ? "No users found for this hub."
                      : "No registered-but-never-ordered users found for this hub."}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <div className="row align-items-end mb-4">
            <div className="col-md-3 mb-2">
              <Form.Label>Amount (₹)</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </div>
            <div className="col-md-4 mb-2">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
              />
            </div>
            <div className="col-md-3 mb-2">
              <Form.Label>Expiry Date</Form.Label>
              <Form.Control
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
            <div className="col-md-2 mb-2">
              <Button
                variant="success"
                className="w-100"
                disabled={!canSubmit || submitting}
                onClick={handleSubmit}
              >
                {submitting ? (
                  <Spinner size="sm" />
                ) : (
                  `Credit ${selectedIds.length || ""} Wallet(s)`
                )}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HubWalletCredit;
